import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, User, ArrowLeft, Gamepad2, AtSign, Check, X as XIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { pendingPlayerLoginUsername, clearPendingPlayerLogin } from '@/lib/switchToChild';
import { lovable } from '@/integrations/lovable';
import { toast } from 'sonner';
import { SeoHead } from '@/components/SeoHead';

import { Logo } from '@/components/Logo';
import { PasswordStrengthMeter, usePasswordStrength } from '@/components/PasswordStrengthMeter';
import { RoleChoiceScreen } from '@/components/auth/RoleChoiceScreen';
import { CoachSignupFlow } from '@/components/auth/CoachSignupFlow';
import { ParentSignupFlow } from '@/components/auth/ParentSignupFlow';

import { validateUsernameFormat, checkUsernameAvailable, sanitizeUsername } from '@/lib/usernameValidation';

type AuthMode = 'login' | 'signup' | 'player';

interface AuthProps {
  onBack?: () => void;
  /** Which tab to open on mount. Landing-page CTAs open 'signup'. */
  initialMode?: AuthMode;
  /** Landing-page CTAs can skip the role-choice screen and land directly on a flow. */
  initialSignupPath?: 'player' | 'coach' | 'parent';
}
type UserRole = 'fan' | 'parent' | 'coach';

const ROLES: { id: UserRole; emoji: string; label: string; description: string }[] = [
  { id: 'fan', emoji: '🏈', label: 'Fan / Player', description: 'I want to learn and track my progress' },
  { id: 'parent', emoji: '👨‍👧‍👦', label: 'Parent', description: 'I manage player profiles for my kids' },
];

export default function Auth({ onBack, initialMode = 'login', initialSignupPath }: AuthProps) {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [selectedRole, setSelectedRole] = useState<UserRole>('fan');
  const [signupPath, setSignupPath] = useState<'player' | 'coach' | 'parent' | null>(initialSignupPath ?? null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const displayName = `${firstName} ${lastName}`.trim();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coachOrgName, setCoachOrgName] = useState('');
  const [coachSport, setCoachSport] = useState('football');

  // Player login fields
  // A parent handing the phone over pre-fills the child's username for them.
  const [playerUsername, setPlayerUsername] = useState(() => {
    const pre = pendingPlayerLoginUsername();
    if (pre) clearPendingPlayerLogin();
    return pre;
  });
  const [playerPin, setPlayerPin] = useState('');
  const passwordStrength = usePasswordStrength(password);

  // Username validation state
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const usernameDebounce = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (mode !== 'signup' || !username) {
      setUsernameError('');
      setUsernameAvailable(null);
      return;
    }
    const format = validateUsernameFormat(username, displayName);
    if (!format.valid) {
      setUsernameError(format.error!);
      setUsernameAvailable(null);
      return;
    }
    setUsernameError('');
    setUsernameAvailable(null);
    setCheckingUsername(true);

    if (usernameDebounce.current) clearTimeout(usernameDebounce.current);
    usernameDebounce.current = setTimeout(async () => {
      const available = await checkUsernameAvailable(username);
      setUsernameAvailable(available);
      if (!available) setUsernameError('Username is already taken');
      setCheckingUsername(false);
    }, 500);

    return () => { if (usernameDebounce.current) clearTimeout(usernameDebounce.current); };
  }, [username, displayName, mode]);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Welcome back!");
      } else if (mode === 'signup') {
        // Validate username
        const formatCheck = validateUsernameFormat(username, displayName);
        if (!formatCheck.valid) {
          toast.error(formatCheck.error!);
          setLoading(false);
          return;
        }
        const available = await checkUsernameAvailable(username);
        if (!available) {
          toast.error('Username is already taken');
          setLoading(false);
          return;
        }
        if (passwordStrength.score < 3) {
          toast.error('Please choose a stronger password');
          setLoading(false);
          return;
        }
        if (passwordStrength.isCommon) {
          toast.error('This password has been found in data breaches. Please choose another.');
          setLoading(false);
          return;
        }
        const fullName = displayName || email;
        const isCoach = selectedRole === 'coach';
        const isParent = selectedRole === 'parent';
        const { data: signUpData, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              username: username,
              user_persona: isCoach ? 'coach' : isParent ? 'parent' : 'fan',
              organization_name: isCoach ? (coachOrgName.trim() || undefined) : undefined,
              primary_sport: isCoach ? coachSport : undefined,
            },
            emailRedirectTo: window.location.origin,
          },
        });
        if (error) throw error;
        if (signUpData.user) {
          await supabase
            .from('profiles')
            .update({
              username,
              display_name: fullName || null,
              user_persona: isCoach ? 'coach' : isParent ? 'parent' : 'fan',
            })
            .eq('user_id', signUpData.user.id);
        }
        toast.success("Account created! Check your email to verify. ✉️");
      }
    } catch (err: any) {
      toast.error(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!playerUsername.trim() || !playerPin.trim()) {
      toast.error('Enter your username and PIN');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('player-login', {
        body: { username: playerUsername, pin: playerPin },
      });
      if (error) throw new Error('Invalid username or PIN');
      if (data?.access_token) {
        await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });
        toast.success("Let's go! 🏆");
      } else {
        throw new Error('Invalid username or PIN');
      }
    } catch (err: any) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (result.error) throw result.error;
    } catch (err: any) {
      toast.error(err.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  // ── New-signup split entry: choose Player / Coach / Parent before anything else ──
  if (mode === 'signup' && signupPath === null) {
    return (
      <RoleChoiceScreen
        onChoose={(role) => {
          setSignupPath(role);
          setSelectedRole(role === 'coach' ? 'coach' : role === 'parent' ? 'parent' : 'fan');
        }}
        onBack={() => { if (initialMode === 'signup') { onBack?.(); } else { setMode('login'); } }}
      />
    );
  }

  if (mode === 'signup' && signupPath === 'coach') {
    return <CoachSignupFlow onExit={() => { window.location.href = '/'; }} />;
  }

  if (mode === 'signup' && signupPath === 'parent') {
    return <ParentSignupFlow onExit={() => setSignupPath(null)} />;
  }


  return (

    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title="Sign in or Sign up — Sportara"
        description="Sign in or create your free Sportara account to start learning sports strategy with bite-sized lessons."
        path="/auth"
        noindex
      />

      {/* Header */}
      <div className="bg-secondary text-secondary-foreground py-8 text-center relative">
        {onBack && (
          <button onClick={onBack} className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary-foreground/70 hover:text-secondary-foreground transition-colors" aria-label="Go back">
            <ArrowLeft className="w-6 h-6" />
          </button>
        )}
        <div className="flex items-center justify-center gap-3 mb-2">
          <Logo className="w-14 h-14" alt="Sportara" />
        </div>
        <h1 className="font-display font-bold text-3xl tracking-tight">
          Sportara — Sign In
        </h1>
        <p className="text-sm text-secondary-foreground/60 font-body mt-1">
          Train your mind. Sharpen your game.
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm space-y-6"
        >
          {/* Mode Toggle */}
          <div className="flex bg-muted rounded-xl p-1 gap-1">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-display font-bold transition-all ${
                mode === 'login' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-display font-bold transition-all ${
                mode === 'signup' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Sign Up
            </button>
            <button
              onClick={() => setMode('player')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-display font-bold transition-all flex items-center justify-center gap-1 ${
                mode === 'player' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
              }`}
            >
              <Gamepad2 className="w-3.5 h-3.5" />
              Player
            </button>
          </div>

          {mode === 'player' ? (
            /* ─── Player Login ─── */
            <>
              <div className="text-center">
                <h2 className="font-display font-bold text-2xl">Player Login</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter the username and PIN your parent/coach set up
                </p>
              </div>

              <form onSubmit={handlePlayerLogin} className="space-y-3">
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Username"
                    value={playerUsername}
                    onChange={(e) => setPlayerUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                    required
                    className="pl-10 h-12 rounded-xl border-2 font-body"
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="password"
                    placeholder="PIN"
                    value={playerPin}
                    onChange={(e) => setPlayerPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    inputMode="numeric"
                    className="pl-10 h-12 rounded-xl border-2 font-body tracking-[0.3em]"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 text-base font-display font-bold rounded-xl"
                >
                  {loading ? 'Signing in...' : 'Start Learning 🚀'}
                </Button>
              </form>

              <p className="text-center text-xs text-muted-foreground font-body">
                Don't have a player account? Ask your parent or coach to create one.
              </p>
            </>
          ) : (
            /* ─── Email/Password Auth ─── */
            <>
              <div className="text-center">
                <h2 className="font-display font-bold text-2xl">
                  {mode === 'login' ? 'Welcome Back' : 'Create Your Account'}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {mode === 'login' ? 'Sign in to continue learning' : 'Choose your role to get started'}
                </p>
              </div>

              {/* Role Picker — signup only */}
              {mode === 'signup' && (
                <div className="space-y-1.5">
                  {ROLES.map(role => (
                    <button
                      key={role.id}
                      onClick={() => setSelectedRole(role.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                        selectedRole === role.id
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                          : 'border-border hover:border-primary/30'
                      }`}
                    >
                      <span className="text-xl">{role.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold text-sm">{role.label}</p>
                        <p className="text-[11px] text-muted-foreground">{role.description}</p>
                      </div>
                      {selectedRole === role.id && (
                        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0">
                          <Check className="w-3 h-3 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Google Button */}
              <Button
                onClick={handleGoogleLogin}
                disabled={loading}
                variant="outline"
                className="w-full h-12 font-body font-semibold text-base border-2 rounded-xl"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </Button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground font-body">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Email Form */}
              <form onSubmit={handleEmailAuth} className="space-y-3">
                {mode === 'signup' && (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="First name"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        required
                        className="pl-10 h-12 rounded-xl border-2 font-body"
                      />
                    </div>
                    <div className="relative flex-1">
                      <Input
                        type="text"
                        placeholder="Last name"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        required
                        className="h-12 rounded-xl border-2 font-body"
                      />
                    </div>
                  </div>
                )}
                {mode === 'signup' && (
                  <div className="space-y-1">
                    <div className="relative">
                      <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Username"
                        value={username}
                        onChange={e => setUsername(sanitizeUsername(e.target.value))}
                        required
                        className={`pl-10 pr-10 h-12 rounded-xl border-2 font-body ${
                          usernameError ? 'border-destructive' : usernameAvailable === true ? 'border-green-500' : ''
                        }`}
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {checkingUsername && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />}
                        {!checkingUsername && usernameAvailable === true && <Check className="w-4 h-4 text-green-500" />}
                        {!checkingUsername && usernameError && <XIcon className="w-4 h-4 text-destructive" />}
                      </div>
                    </div>
                    {usernameError && (
                      <p className="text-xs text-destructive font-body pl-1">{usernameError}</p>
                    )}
                    {!usernameError && usernameAvailable === true && (
                      <p className="text-xs text-green-600 font-body pl-1">Username available ✓</p>
                    )}
                  </div>
                )}

                {/* Coach-specific fields */}
                <AnimatePresence>
                  {mode === 'signup' && selectedRole === 'coach' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-3 overflow-hidden"
                    >
                      <Input
                        type="text"
                        placeholder="Organization / School name (optional)"
                        value={coachOrgName}
                        onChange={e => setCoachOrgName(e.target.value)}
                        className="h-12 rounded-xl border-2 font-body"
                      />
                      <div className="flex gap-2">
                        {['football', 'basketball', 'soccer'].map(s => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setCoachSport(s)}
                            className={`flex-1 py-2 rounded-lg text-xs font-display font-bold border-2 transition-all ${
                              coachSport === s ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground'
                            }`}
                          >
                            {s === 'basketball' ? '🏀' : s === 'soccer' ? '⚽' : '🏈'} {s.charAt(0).toUpperCase() + s.slice(1)}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="pl-10 h-12 rounded-xl border-2 font-body"
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="pl-10 pr-10 h-12 rounded-xl border-2 font-body"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {mode === 'signup' && (
                  <PasswordStrengthMeter password={password} show={true} />
                )}
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!email) {
                        toast.error('Enter your email first');
                        return;
                      }
                      setLoading(true);
                      try {
                        const { error } = await supabase.auth.resetPasswordForEmail(email, {
                          redirectTo: `${window.location.origin}/reset-password`,
                        });
                        if (error) throw error;
                        toast.success('Password reset link sent! Check your email. ✉️');
                      } catch (err: any) {
                        toast.error(err.message || 'Failed to send reset email');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="text-xs text-primary hover:underline font-body self-end"
                  >
                    Forgot password?
                  </button>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 text-base font-display font-bold rounded-xl"
                >
                  {loading ? 'Loading...' : mode === 'login' ? 'Sign In' : `Create ${selectedRole === 'coach' ? 'Coach' : selectedRole === 'parent' ? 'Parent' : ''} Account`}
                </Button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
